import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import useMediaQuery from '@mui/material/useMediaQuery';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import MembershipBookingOptionLabel, { membershipOptionLabelSx } from './MembershipBookingOptionLabel';
import StoreService from '../../services/storeService';
import ServiceComboService, { mergeCatalogCombos } from '../../services/serviceComboService';
import MembershipPlanService from '../../services/membershipPlanService';
import ServicePackageService from '../../services/servicePackageService';
import { getApiErrorCode, isApiErrorCode } from '../../utils/apiErrorCode';
import { describeInsufficientStock } from '../../utils/stockErrorUtils';
import { MANAGEMENT_ENTITLEMENT_ERROR_KEYS } from '../../utils/entitlementErrorKeys';
import { updateManagementAppointment } from '../../services/appointmentService';
import { getManagementMemberships } from '../../services/storeCustomerMembershipService';
import { getServiceName } from '../../utils/serviceTranslations';
import { normalizeLanguage, DEFAULT_LANGUAGE, getLocalizedValue } from '../../constants/Languages';
import {
  buildManagementAppointmentMembershipUpdateFields,
  getEnrollablePlans,
  getHeldCustomerMemberships,
  getLockedServiceIds,
  getProfessionalsForManagedBookingService,
  PACKAGE_OPTION_PREFIX,
  toPackageLockSource,
  mergeLockedServiceIds,
  toggleServiceIdWithLocks,
} from '../../utils/managementBookingMembershipUtils';
import {
  applyLineDurations,
  buildPerServiceAssignments,
  getManagementBookingWarning,
  MANAGEMENT_START_TIME_OPTIONS,
  parseDurationToMinutes,
  parseTimeToMinutes,
  sortServicesForBooking,
  withLineQuotes,
} from '../../utils/appointmentBookingUtils';
import AppointmentLineQuoteFields from './AppointmentLineQuoteFields';
import { Permissions } from '../../constants/Permissions';
import { usePermissions } from '../../hooks/usePermissions';
import {
  ServiceValueMode,
  assignedServicePriceHint,
  isLineQuoteDurationInvalid,
  isSchedulableDuration,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';
import { getAppointmentCheckoutBreakdown, getPendingAppointmentProductLines } from '../../utils/appointmentCheckoutUtils';
import ProductSaleLinesField from '../products/ProductSaleLinesField';
import ProductService from '../../services/productService';
import useStorePlan from '../../hooks/useStorePlan';

const drawerFooterHeight = 92;

const sectionPaperSx = {
  borderRadius: BRAND_RADIUS.card,
  border: `1px solid ${BRAND.line}`,
  bgcolor: BRAND.surface,
  p: 2,
};

const parseDateKey = (dateKey) => {
  if (!dateKey) {
    return null;
  }

  return new Date(`${dateKey}T12:00:00`);
};

const formatDateKey = (date) => {
  if (!date) {
    return null;
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const EditAppointmentDialog = ({
  open,
  onClose,
  appointment,
  storeId,
  teamMembers,
  workingHoursByTeamMemberId,
  storeDetails,
  storeLanguage,
  existingAppointments = [],
  onUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const lang = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const normalizedStoreLanguage = normalizeLanguage(storeLanguage) || DEFAULT_LANGUAGE;
  const { isPro, entitlements } = useStorePlan(storeId, Boolean(open && storeId));
  const { hasPermission } = usePermissions(storeId);
  const canSetServicePrices = hasPermission(Permissions.AppointmentsPriceUpdate);
  const allowsMemberships = isPro && entitlements?.allowsMemberships !== false;

  const [services, setServices] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [lineQuotes, setLineQuotes] = useState({});
  const [serviceProfessionalMap, setServiceProfessionalMap] = useState({});
  const [detailsFromCustomer, setDetailsFromCustomer] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [startTimeMenuOpen, setStartTimeMenuOpen] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [customerMemberships, setCustomerMemberships] = useState([]);
  const [selectedMembershipId, setSelectedMembershipId] = useState('');
  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState('');
  const [packageEntitlements, setPackageEntitlements] = useState([]);
  const [selectedServicePackageId, setSelectedServicePackageId] = useState('');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productLines, setProductLines] = useState([]);
  // The customer paid for these products online, so they are handed over as booked.
  const productsPaidOnline = getAppointmentCheckoutBreakdown(appointment).productsPaidOnline > 0;
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startTimeAnchorRef = useRef(null);

  const activeTeamMembers = useMemo(
    () => (teamMembers || []).filter((member) => member.isActive !== false),
    [teamMembers],
  );

  const selectedServices = useMemo(
    () => sortServicesForBooking(services.filter((service) => selectedServiceIds.includes(service.id))),
    [services, selectedServiceIds],
  );
  const quotedServices = useMemo(
    () => applyLineDurations(selectedServices, lineQuotes),
    [selectedServices, lineQuotes],
  );

  useEffect(() => {
    setLineQuotes((current) => {
      const next = { ...current };
      let changed = false;
      selectedServices.forEach((service) => {
        if (service.isCombo || next[service.id]) return;
        const durationMode = normalizeServiceValueMode(service.durationMode);
        const catalogMinutes = parseDurationToMinutes(service.duration);
        next[service.id] = {
          durationMinutes: durationMode === ServiceValueMode.Fixed && isSchedulableDuration(catalogMinutes)
            ? catalogMinutes
            : '',
          unitPrice: '',
          priceTouched: false,
        };
        changed = true;
      });
      return changed ? next : current;
    });
  }, [selectedServices]);

  const selectedHeldMembership = useMemo(
    () => customerMemberships.find((membership) => membership.id === selectedMembershipId) || null,
    [customerMemberships, selectedMembershipId],
  );

  const selectedPlan = useMemo(
    () => membershipPlans.find((plan) => (
      plan.id === selectedMembershipPlanId
      || plan.id === selectedHeldMembership?.membershipPlanId
    )) || null,
    [membershipPlans, selectedMembershipPlanId, selectedHeldMembership],
  );

  // The package the appointment already uses stays listed even once it is used up or expired:
  // keeping it on an unchanged booking is still allowed.
  const packageOptions = useMemo(() => {
    const currentPackageId = appointment?.customerServicePackageId;
    if (!currentPackageId || packageEntitlements.some((entitlement) => entitlement.customerServicePackageId === currentPackageId)) {
      return packageEntitlements;
    }

    return [
      {
        customerServicePackageId: currentPackageId,
        name: appointment.servicePackageName || {},
        remainingQuantity: null,
        isExpiredForDate: false,
        requiredServiceIds: (appointment.services || [])
          .filter((service) => service.coveredByPackage)
          .map((service) => Number(service.serviceId)),
        allowedTeamMemberIds: [],
      },
      ...packageEntitlements,
    ];
  }, [appointment, packageEntitlements]);

  const selectedPackageEntitlement = useMemo(
    () => packageOptions.find((entitlement) => entitlement.customerServicePackageId === selectedServicePackageId) || null,
    [packageOptions, selectedServicePackageId],
  );

  // The plan or package being redeemed keeps its services in the booking and its professionals.
  const entitlementLockSource = useMemo(
    () => selectedPlan || toPackageLockSource(selectedPackageEntitlement),
    [selectedPlan, selectedPackageEntitlement],
  );

  const lockedServiceIds = useMemo(
    () => getLockedServiceIds(entitlementLockSource),
    [entitlementLockSource],
  );

  const enrollablePlans = useMemo(
    () => getEnrollablePlans(membershipPlans, customerMemberships),
    [membershipPlans, customerMemberships],
  );

  const hideProfessionalSection = activeTeamMembers.length <= 1;
  const hasPackageOptions = Boolean(appointment?.customerId) && packageOptions.length > 0;
  const showMembershipSection = hasPackageOptions || (allowsMemberships
    && Boolean(appointment?.customerId)
    && (membershipPlans.length > 0 || customerMemberships.length > 0));

  const bookingWarning = useMemo(() => {
    if (!selectedDate || !selectedStartTime) {
      return null;
    }

    return getManagementBookingWarning({
      store: storeDetails,
      date: formatDateKey(selectedDate),
      startTime: selectedStartTime,
      selectedServices: quotedServices,
      serviceProfessionalMap,
      existingAppointments,
      excludeAppointmentId: appointment?.id,
      workingHoursByTeamMemberId,
    });
  }, [
    storeDetails,
    workingHoursByTeamMemberId,
    selectedDate,
    selectedStartTime,
    quotedServices,
    serviceProfessionalMap,
    existingAppointments,
    appointment?.id,
  ]);

  useEffect(() => {
    if (!open || !appointment) {
      return;
    }

    const appointmentServices = appointment.services || [];
    setSelectedDate(parseDateKey(appointment.date));
    setSelectedServiceIds(appointmentServices.map((service) => service.serviceId));
    setLineQuotes(Object.fromEntries(appointmentServices.filter((service) => service.serviceId).map((service) => {
      const from = parseTimeToMinutes(service.period?.from);
      const to = parseTimeToMinutes(service.period?.to);
      const minutes = to > from ? to - from : '';
      return [service.serviceId, {
        durationMinutes: minutes,
        unitPrice: service.unitPrice == null ? '' : String(service.unitPrice),
        priceTouched: false,
      }];
    })));
    setServiceProfessionalMap(
      Object.fromEntries(
        appointmentServices.map((service) => [service.serviceId, service.teamMemberId]),
      ),
    );
    setDetailsFromCustomer(appointment.detailsFromCustomer || '');
    setProductLines(
      getPendingAppointmentProductLines(appointment).map((item) => ({
        productId: item.productId,
        quantity: item.quantity || 1,
      })),
    );
    const initialStart = appointmentServices[0]?.period?.from || appointment.period?.from || '';
    setSelectedStartTime(initialStart ? initialStart.slice(0, 5) : '');
    setErrors({});
    setSubmitError(null);

    setSelectedServicePackageId(appointment.customerServicePackageId || '');
    if (appointment.customerServicePackageId) {
      setSelectedMembershipId('');
      setSelectedMembershipPlanId('');
    } else if (appointment.pendingMembershipEnrollment && appointment.pendingMembershipPlanId) {
      setSelectedMembershipId('');
      setSelectedMembershipPlanId(appointment.pendingMembershipPlanId);
    } else if (appointment.customerMembershipId) {
      setSelectedMembershipId(appointment.customerMembershipId);
      setSelectedMembershipPlanId('');
    } else {
      setSelectedMembershipId('');
      setSelectedMembershipPlanId('');
    }
  }, [open, appointment]);

  useEffect(() => {
    if (!open || !storeId) {
      return undefined;
    }

    let cancelled = false;

    const loadData = async () => {
      try {
        setLoadingData(true);
        const [storeServices, combos, plansData, catalogData] = await Promise.all([
          StoreService.getServices(true),
          ServiceComboService.getServiceComboCatalog(storeId).catch(() => []),
          allowsMemberships
            ? MembershipPlanService.getActivePlans(storeId).catch(() => [])
            : Promise.resolve([]),
          ProductService.getProductCatalog(storeId).catch(() => []),
        ]);
        if (!cancelled) {
          setServices(mergeCatalogCombos(storeServices, combos));
          setMembershipPlans(Array.isArray(plansData) ? plansData : []);
          setCatalogProducts(Array.isArray(catalogData)
            ? catalogData.filter((product) => !product.disabled)
            : []);
        }
      } catch (loadError) {
        console.error('Failed to load services for edit appointment:', loadError);
        if (!cancelled) {
          setSubmitError('managementAppointments.edit.loadError');
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [allowsMemberships, open, storeId]);

  useEffect(() => {
    if (!open || !storeId || !allowsMemberships || !appointment?.customerId) {
      setCustomerMemberships([]);
      return undefined;
    }

    let cancelled = false;
    getManagementMemberships(storeId)
      .then((memberships) => {
        if (!cancelled) {
          setCustomerMemberships(getHeldCustomerMemberships(memberships, appointment.customerId));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCustomerMemberships([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [allowsMemberships, open, storeId, appointment?.customerId]);

  const selectedDateKey = selectedDate ? formatDateKey(selectedDate) : null;

  // Packages already paid for can be redeemed even after the store drops to Free.
  useEffect(() => {
    if (!open || !storeId || !appointment?.customerId) {
      setPackageEntitlements((current) => (current.length === 0 ? current : []));
      return undefined;
    }

    let cancelled = false;
    ServicePackageService.getManagementBookingEntitlements(storeId, {
      customerId: appointment.customerId,
      serviceIds: selectedServiceIds,
      date: selectedDateKey,
      appointmentId: appointment.id,
    })
      .then((entitlements) => {
        if (!cancelled) setPackageEntitlements(entitlements);
      })
      .catch(() => {
        if (!cancelled) setPackageEntitlements([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, storeId, appointment?.customerId, appointment?.id, selectedServiceIds, selectedDateKey]);

  useEffect(() => {
    if (!open || selectedServiceIds.length === 0) {
      return;
    }

    setServiceProfessionalMap((current) => {
      const next = { ...current };
      let changed = false;
      selectedServiceIds.forEach((serviceId) => {
        if (next[serviceId]) {
          return;
        }

        if (activeTeamMembers.length === 1) {
          next[serviceId] = activeTeamMembers[0].id;
          changed = true;
          return;
        }

        const service = services.find((item) => item.id === serviceId);
        const candidates = getProfessionalsForManagedBookingService(
          activeTeamMembers,
          serviceId,
          entitlementLockSource,
          service,
        );
        if (candidates.length === 1) {
          next[serviceId] = candidates[0].id;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [open, selectedServiceIds, activeTeamMembers, entitlementLockSource, services]);

  const toggleService = (serviceId) => {
    setSelectedServiceIds((current) => {
      const next = toggleServiceIdWithLocks(current, serviceId, lockedServiceIds);
      const removed = current.filter((id) => !next.includes(Number(id)) && !next.includes(id));
      if (removed.length > 0) {
        setServiceProfessionalMap((map) => {
          const updated = { ...map };
          removed.forEach((id) => {
            delete updated[id];
          });
          return updated;
        });
      }
      return next;
    });
    setErrors((current) => ({ ...current, services: null }));
    setSubmitError(null);
  };

  const applyPackageSelection = (nextPackageId) => {
    setSelectedMembershipId('');
    setSelectedMembershipPlanId('');
    setSelectedServicePackageId(nextPackageId || '');
    const nextPackage = packageOptions.find((entitlement) => entitlement.customerServicePackageId === nextPackageId);
    if (nextPackage) {
      setSelectedServiceIds((current) => mergeLockedServiceIds(current, getLockedServiceIds(toPackageLockSource(nextPackage))));
    }
  };

  const applyMembershipSelection = (nextMembershipId, nextPlanId) => {
    setSelectedServicePackageId('');
    setSelectedMembershipId(nextMembershipId || '');
    setSelectedMembershipPlanId(nextPlanId || '');
    const nextPlan = membershipPlans.find((plan) => (
      plan.id === nextPlanId
      || customerMemberships.find((membership) => membership.id === nextMembershipId)?.membershipPlanId === plan.id
    ));
    if (nextPlan) {
      setSelectedServiceIds((current) => mergeLockedServiceIds(current, getLockedServiceIds(nextPlan)));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!selectedDate) {
      nextErrors.date = 'managementAppointments.edit.validation.dateRequired';
    }

    if (!selectedStartTime) {
      nextErrors.startTime = 'managementAppointments.edit.validation.startTimeRequired';
    }

    if (selectedServiceIds.length === 0) {
      nextErrors.services = 'managementAppointments.book.validation.servicesRequired';
    }

    selectedServices.forEach((service) => {
      if (service.isCombo) return;
      const quote = lineQuotes[service.id];
      if (isLineQuoteDurationInvalid(service, quote)) {
        nextErrors.duration = 'managementAppointments.book.validation.durationRequired';
      }
      if (canSetServicePrices && quote?.priceTouched && quote.unitPrice !== '' && quote.unitPrice != null) {
        const price = Number(quote.unitPrice);
        if (!Number.isFinite(price) || price < 0) {
          nextErrors.linePrice = 'managementAppointments.book.validation.linePriceInvalid';
        }
      }
    });

    const missingProfessional = selectedServiceIds.some(
      (serviceId) => !serviceProfessionalMap[serviceId],
    );

    if (missingProfessional) {
      nextErrors.professionals = 'managementAppointments.book.validation.professionalRequired';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!appointment?.id || !validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const serviceAssignments = withLineQuotes(
        buildPerServiceAssignments(
          quotedServices,
          serviceProfessionalMap,
          selectedStartTime,
        ),
        lineQuotes,
        canSetServicePrices,
      );

      const payload = {
        date: formatDateKey(selectedDate),
        serviceAssignments,
        detailsFromCustomer: detailsFromCustomer.trim(),
        products: productsPaidOnline ? undefined : productLines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
        })),
        ...buildManagementAppointmentMembershipUpdateFields({
          selectedMembershipId,
          selectedMembershipPlanId,
          hadMembershipCoverage: Boolean(
            appointment.customerMembershipId
            || appointment.pendingMembershipPlanId
            || appointment.coveredByMembership
            || appointment.pendingMembershipEnrollment
          ),
          selectedServicePackageId,
          hadPackageCoverage: Boolean(appointment.customerServicePackageId || appointment.coveredByServicePackage),
        }),
      };

      await updateManagementAppointment(appointment.id, payload, storeId);

      onUpdated?.();
      onClose();
    } catch (submitErr) {
      console.error('Failed to update appointment:', submitErr);
      const apiMessage = submitErr?.response?.data?.message || '';
      const stockMessage = describeInsufficientStock(submitErr, t);
      const entitlementErrorKey = MANAGEMENT_ENTITLEMENT_ERROR_KEYS[getApiErrorCode(submitErr)];
      if (stockMessage) {
        setSubmitError(stockMessage);
      } else if (entitlementErrorKey) {
        setSubmitError(entitlementErrorKey);
      } else if (isApiErrorCode(submitErr, 'AppointmentProductsPaidOnline')) {
        setSubmitError('managementAppointments.prepayment.productsPaidOnlineLocked');
      } else if (apiMessage.toLowerCase().includes('no longer available')) {
        setSubmitError('managementAppointments.book.warnings.slotConflict');
      } else {
        setSubmitError(
          submitErr?.response?.data?.error
          || apiMessage
          || 'managementAppointments.edit.submitError',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : { sm: 520, md: 620 },
          maxWidth: '100vw',
          height: isMobile ? 'min(92dvh, 920px)' : '100%',
          maxHeight: isMobile ? '92dvh' : '100%',
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
          overflow: 'hidden',
          background: BRAND.surface,
          boxShadow: isMobile
            ? `0 -12px 40px ${brandAlpha('primary', 0.16)}`
            : `0 0 40px ${brandAlpha('primary', 0.12)}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            p: { xs: 2.25, sm: 3 },
            background: BRAND.primary,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: BRAND.onPrimary,
              bgcolor: alpha('#000', 0.12),
              '&:hover': { bgcolor: alpha('#000', 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ pr: 5 }}>
            <Typography
              variant="overline"
              sx={{ color: BRAND.onPrimaryMuted, letterSpacing: '0.14em', fontWeight: 700 }}
            >
              {t('managementAppointments.edit.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: BRAND.onPrimary,
                fontWeight: 800,
                lineHeight: 1.1,
                fontSize: { xs: '1.65rem', sm: '2rem' },
                mt: 0.25,
              }}
            >
              {appointment?.customerName || '—'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2, sm: 3 },
            py: { xs: 2.25, sm: 3 },
            pb: `calc(${drawerFooterHeight}px + 16px)`,
          }}
        >
          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {submitError && (
                <Alert severity="error">
                  {t(submitError, { defaultValue: submitError })}
                </Alert>
              )}

              <Paper elevation={0} sx={sectionPaperSx}>
                <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                  {t('managementAppointments.edit.date')} *
                </Typography>
                <SalovareDatePicker
                  value={selectedDate}
                  onChange={(value) => {
                    setSelectedDate(value);
                    setErrors((current) => ({ ...current, date: null }));
                  }}
                  error={Boolean(errors.date)}
                  helperText={errors.date ? t(errors.date) : ''}
                />
              </Paper>

              <Paper elevation={0} sx={sectionPaperSx}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setStartTimeMenuOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setStartTimeMenuOpen(true);
                    }
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('managementAppointments.book.startTime')} *
                  </Typography>
                  <Stack ref={startTimeAnchorRef} direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 18, color: BRAND.accentText }} />
                    <Typography variant="h6" sx={{ fontWeight: 900, color: BRAND.text }}>
                      {selectedStartTime || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('managementAppointments.book.tapToChangeTime')}
                    </Typography>
                  </Stack>
                  {errors.startTime && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {t(errors.startTime)}
                    </Typography>
                  )}
                </Box>

                <Menu
                  open={startTimeMenuOpen}
                  onClose={() => setStartTimeMenuOpen(false)}
                  anchorEl={startTimeAnchorRef.current}
                  disableScrollLock
                  PaperProps={{ sx: { maxHeight: 360 } }}
                >
                  {MANAGEMENT_START_TIME_OPTIONS.map((time) => (
                    <MenuItem
                      key={time}
                      selected={time === selectedStartTime}
                      onClick={() => {
                        setSelectedStartTime(time);
                        setStartTimeMenuOpen(false);
                      }}
                    >
                      {time}
                    </MenuItem>
                  ))}
                </Menu>
              </Paper>

              {bookingWarning && (
                <Alert severity="warning">
                  {t(`managementAppointments.book.warnings.${bookingWarning}`)}
                </Alert>
              )}

              {showMembershipSection && (
                <Paper elevation={0} sx={sectionPaperSx}>
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t(hasPackageOptions ? 'managementAppointments.book.membershipOrPackage' : 'managementAppointments.book.membership')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t(hasPackageOptions ? 'managementAppointments.book.membershipOrPackageHelp' : 'managementAppointments.book.membershipHelp')}
                  </Typography>
                  <RadioGroup
                    sx={{ width: '100%' }}
                    value={
                      (selectedServicePackageId && `${PACKAGE_OPTION_PREFIX}${selectedServicePackageId}`)
                      || (selectedMembershipPlanId && `plan:${selectedMembershipPlanId}`)
                      || (selectedMembershipId && `membership:${selectedMembershipId}`)
                      || ''
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value.startsWith(PACKAGE_OPTION_PREFIX)) {
                        applyPackageSelection(value.slice(PACKAGE_OPTION_PREFIX.length));
                        return;
                      }
                      if (value.startsWith('plan:')) {
                        applyMembershipSelection('', value.slice(5));
                        return;
                      }
                      if (value.startsWith('membership:')) {
                        applyMembershipSelection(value.slice(11), '');
                        return;
                      }
                      applyMembershipSelection('', '');
                    }}
                  >
                    <FormControlLabel
                      value=""
                      control={<Radio />}
                      label={t(hasPackageOptions
                        ? 'managementAppointments.book.noEntitlementRedemption'
                        : 'managementAppointments.book.noMembershipRedemption')}
                    />
                    {packageOptions.map((entitlement) => (
                      <FormControlLabel
                        key={entitlement.customerServicePackageId}
                        value={`${PACKAGE_OPTION_PREFIX}${entitlement.customerServicePackageId}`}
                        control={<Radio />}
                        disabled={entitlement.isExpiredForDate}
                        sx={membershipOptionLabelSx}
                        label={(
                          <MembershipBookingOptionLabel
                            name={getLocalizedValue(
                              entitlement.name,
                              lang,
                              normalizedStoreLanguage,
                              t('customer.packages.card.unnamed'),
                            )}
                            tag={(entitlement.isExpiredForDate && t('managementAppointments.book.packageExpiredTag'))
                              || (entitlement.remainingQuantity == null
                                ? t('managementAppointments.book.packageInUseTag')
                                : t('managementAppointments.book.packageTag', { count: entitlement.remainingQuantity }))}
                            variant={entitlement.isExpiredForDate ? 'enroll' : 'subscribed'}
                          />
                        )}
                      />
                    ))}
                    {customerMemberships.map((membership) => (
                      <FormControlLabel
                        key={membership.id}
                        value={`membership:${membership.id}`}
                        control={<Radio />}
                        sx={membershipOptionLabelSx}
                        label={(
                          <MembershipBookingOptionLabel
                            name={getLocalizedValue(
                              membership.planName,
                              lang,
                              normalizedStoreLanguage,
                              t('customer.memberships.card.unnamed'),
                            )}
                            tag={t('managementAppointments.book.subscribedTag')}
                            variant="subscribed"
                          />
                        )}
                      />
                    ))}
                    {enrollablePlans.map((plan) => (
                      <FormControlLabel
                        key={plan.id}
                        value={`plan:${plan.id}`}
                        control={<Radio />}
                        sx={membershipOptionLabelSx}
                        label={(
                          <MembershipBookingOptionLabel
                            name={getLocalizedValue(
                              plan.name,
                              lang,
                              normalizedStoreLanguage,
                              t('customer.memberships.card.unnamed'),
                            )}
                            tag={t('managementAppointments.book.enrollInStoreTag')}
                            variant="enroll"
                          />
                        )}
                      />
                    ))}
                  </RadioGroup>
                  {selectedMembershipPlanId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('managementAppointments.book.pendingEnrollmentHelp')}
                    </Typography>
                  )}
                </Paper>
              )}

              <Paper elevation={0} sx={sectionPaperSx}>
                <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                  {t('managementAppointments.book.services')} *
                </Typography>
                {entitlementLockSource && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t(selectedPlan
                      ? 'managementAppointments.book.planServicesLocked'
                      : 'managementAppointments.book.packageServicesLocked')}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {services.map((service) => {
                    const selected = selectedServiceIds.includes(service.id);
                    const locked = selected && lockedServiceIds.has(Number(service.id));
                    return (
                      <Chip
                        key={service.id}
                        label={getServiceName(service.name, lang, normalizedStoreLanguage)}
                        onClick={locked ? undefined : () => toggleService(service.id)}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={selected ? { bgcolor: BRAND.accent, color: BRAND.onAccent } : undefined}
                      />
                    );
                  })}
                </Box>
                {errors.services && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {t(errors.services)}
                  </Typography>
                )}
              </Paper>

              {selectedServices.length > 0 && !hideProfessionalSection && (
                <Paper elevation={0} sx={sectionPaperSx}>
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t('managementAppointments.book.professionals')}
                  </Typography>
                  {selectedServices.map((service) => {
                    const candidates = getProfessionalsForManagedBookingService(
                      activeTeamMembers,
                      service.id,
                      entitlementLockSource,
                      service,
                    );
                    const options = service.isCombo || candidates.length > 0 ? candidates : activeTeamMembers;

                    return (
                      <SalovareSelectField
                        key={service.id}
                        label={getServiceName(service.name, lang, normalizedStoreLanguage)}
                        required
                        value={serviceProfessionalMap[service.id] || ''}
                        onChange={(event) => {
                          setServiceProfessionalMap((current) => ({
                            ...current,
                            [service.id]: event.target.value,
                          }));
                          setErrors((current) => ({ ...current, professionals: null }));
                        }}
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) {
                            return (
                              <Typography component="span" sx={{ color: BRAND.textSubtle }}>
                                {t('managementAppointments.book.selectProfessional')}
                              </Typography>
                            );
                          }
                          return options.find((member) => member.id === value)?.name || '';
                        }}
                      >
                        {options.map((member) => (
                          <MenuItem key={member.id} value={member.id}>
                            {member.name}
                          </MenuItem>
                        ))}
                      </SalovareSelectField>
                    );
                  })}
                  {errors.professionals && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {t(errors.professionals)}
                    </Typography>
                  )}
                </Paper>
              )}

              {selectedServices.some((service) => !service.isCombo) ? (
                <Paper elevation={0} sx={sectionPaperSx}>
                  {selectedServices.filter((service) => !service.isCombo).map((service) => (
                    <AppointmentLineQuoteFields
                      key={service.id}
                      service={service}
                      serviceName={getServiceName(service.name, lang, normalizedStoreLanguage)}
                      quote={lineQuotes[service.id]}
                      canSetPrice={canSetServicePrices}
                      currency={storeDetails?.currency}
                      priceHint={assignedServicePriceHint(
                        service,
                        activeTeamMembers.find((member) => member.id === serviceProfessionalMap[service.id]),
                      )}
                      onChange={(next) => setLineQuotes((current) => ({ ...current, [service.id]: next }))}
                    />
                  ))}
                  {errors.duration ? (
                    <Typography variant="caption" color="error">{t(errors.duration)}</Typography>
                  ) : null}
                  {errors.linePrice ? (
                    <Typography variant="caption" color="error">{t(errors.linePrice)}</Typography>
                  ) : null}
                </Paper>
              ) : null}

              {(catalogProducts.length > 0 || productLines.length > 0) && (
                <Paper elevation={0} sx={sectionPaperSx}>
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t('managementAppointments.book.products')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t(productsPaidOnline
                      ? 'managementAppointments.prepayment.productsPaidOnlineLocked'
                      : 'managementAppointments.book.productsHelp')}
                  </Typography>
                  <ProductSaleLinesField
                    products={catalogProducts}
                    lines={productLines}
                    onChange={setProductLines}
                    readOnly={productsPaidOnline}
                    currencyCode={storeDetails?.currency}
                    disableOutOfStock
                  />
                </Paper>
              )}

              <Paper elevation={0} sx={sectionPaperSx}>
                <Box sx={inputWrapperSx}>
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('managementAppointments.book.notes')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={detailsFromCustomer}
                    onChange={(event) => setDetailsFromCustomer(event.target.value)}
                    placeholder={t('managementAppointments.book.notesPlaceholder')}
                  />
                </Box>
              </Paper>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: `1px solid ${BRAND.line}`,
            bgcolor: BRAND.surface,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Button color="inherit" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSubmit} disabled={submitting || loadingData}>
            {submitting ? t('managementAppointments.edit.submitting') : t('managementAppointments.edit.submit')}
          </PrimaryActionButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditAppointmentDialog;
